/**
 * Public essay listing page showing all published essays.
 * 
 * Features:
 * - Paginated list of published essays
 * - Filter by category and tag
 * - Search functionality
 * - Beautiful card grid layout
 * 
 * Called by:
 * - App.tsx as /essays route
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Typography, Spin, Alert, Card, Tag, Space, Row, Col, Pagination, Select } from 'antd';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getPublicEssays, getPublicCategories, getPublicTags, type PublicEssayListItem } from '../../services/api';
import { format } from 'date-fns';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PAGE_SIZE = 12;

/**
 * Essay card component.
 */
function EssayCard({ essay }: { essay: PublicEssayListItem }) {
  return (
    <Link to={`/essays/${essay.slug}`} style={{ textDecoration: 'none' }}>
      <Card
        hoverable
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          height: '100%',
          transition: 'all 0.3s ease',
        }}
        bodyStyle={{ padding: '24px' }}
        className="essay-card"
      >
        {/* Category badge */}
        {essay.category && (
          <Tag
            style={{
              background: 'rgba(212, 175, 55, 0.15)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              color: '#d4af37',
              marginBottom: '12px',
            }}
          >
            {essay.category.name}
          </Tag>
        )}

        {/* Title */}
        <Title
          level={4}
          style={{
            color: '#f5f5f5',
            marginBottom: '8px',
            fontFamily: "var(--font-serif)",
          }}
        >
          {essay.title}
        </Title>

        {/* Preview text */}
        <Paragraph
          ellipsis={{ rows: 3 }}
          style={{
            color: '#a0a0a0',
            fontSize: '0.95rem',
            lineHeight: 1.7,
            marginBottom: '16px',
          }}
        >
          {essay.preview}
        </Paragraph>

        {/* Meta info */}
        <Space size="middle" style={{ color: '#666' }}>
          {essay.publishedAt && (
            <span>
              <CalendarOutlined style={{ marginRight: '4px' }} />
              {format(new Date(essay.publishedAt), 'MMM d, yyyy')}
            </span>
          )}
          <span>
            <ClockCircleOutlined style={{ marginRight: '4px' }} />
            {essay.readingTime} min read
          </span>
        </Space>

        {/* Tags */}
        {essay.tags.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {essay.tags.slice(0, 3).map((tag) => (
              <Tag
                key={tag.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#888',
                  fontSize: '0.75rem',
                }}
              >
                {tag.name}
              </Tag>
            ))}
          </div>
        )}
      </Card>
    </Link>
  );
}

/**
 * Public essay list page component.
 */
function PublicEssayListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  
  const category = searchParams.get('category') || undefined;
  const tag = searchParams.get('tag') || undefined;

  // Fetch essays
  const {
    data: essaysData,
    isLoading: essaysLoading,
    error: essaysError,
  } = useQuery({
    queryKey: ['public', 'essays', page, category, tag],
    queryFn: () => getPublicEssays({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      category,
      tag,
    }),
  });

  // Fetch categories for filter
  const { data: categories } = useQuery({
    queryKey: ['public', 'categories'],
    queryFn: getPublicCategories,
  });

  // Fetch tags for filter
  const { data: tags } = useQuery({
    queryKey: ['public', 'tags'],
    queryFn: getPublicTags,
  });

  /**
   * Handle category filter change.
   */
  function handleCategoryChange(value: string | undefined) {
    if (value) {
      searchParams.set('category', value);
    } else {
      searchParams.delete('category');
    }
    setSearchParams(searchParams);
    setPage(1);
  }

  /**
   * Handle tag filter change.
   */
  function handleTagChange(value: string | undefined) {
    if (value) {
      searchParams.set('tag', value);
    } else {
      searchParams.delete('tag');
    }
    setSearchParams(searchParams);
    setPage(1);
  }

  /**
   * Handle page change.
   */
  function handlePageChange(newPage: number) {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: '48px' }}>
        <Title
          style={{
            fontSize: '2.5rem',
            fontFamily: "var(--font-serif)",
            fontWeight: 400,
            color: '#f5f5f5',
            marginBottom: '16px',
          }}
        >
          All Essays
        </Title>
        <Paragraph style={{ color: '#a0a0a0', fontSize: '1.1rem' }}>
          Browse through the complete collection of published essays.
        </Paragraph>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: '32px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <Select
          placeholder="Filter by category"
          allowClear
          value={category}
          onChange={handleCategoryChange}
          style={{ minWidth: '200px' }}
        >
          {categories?.map((cat) => (
            <Option key={cat.id} value={cat.slug}>
              {cat.name} ({cat.essayCount})
            </Option>
          ))}
        </Select>

        <Select
          placeholder="Filter by tag"
          allowClear
          value={tag}
          onChange={handleTagChange}
          style={{ minWidth: '200px' }}
        >
          {tags?.map((t) => (
            <Option key={t.id} value={t.slug}>
              {t.name} ({t.essayCount})
            </Option>
          ))}
        </Select>
      </div>

      {/* Loading state */}
      {essaysLoading && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" />
        </div>
      )}

      {/* Error state */}
      {essaysError && (
        <Alert
          message="Error loading essays"
          description="Unable to load essays. Please try again later."
          type="error"
          showIcon
        />
      )}

      {/* Empty state */}
      {essaysData && essaysData.essays.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text type="secondary">
            {category || tag ? 'No essays match the selected filters.' : 'No published essays yet. Check back soon!'}
          </Text>
        </div>
      )}

      {/* Essay grid */}
      {essaysData && essaysData.essays.length > 0 && (
        <>
          <Row gutter={[24, 24]}>
            {essaysData.essays.map((essay) => (
              <Col key={essay.id} xs={24} md={12} lg={8}>
                <EssayCard essay={essay} />
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          {essaysData.total > PAGE_SIZE && (
            <div style={{ textAlign: 'center', marginTop: '48px' }}>
              <Pagination
                current={page}
                total={essaysData.total}
                pageSize={PAGE_SIZE}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}

      {/* CSS for hover effects */}
      <style>{`
        .essay-card:hover {
          border-color: rgba(212, 175, 55, 0.3) !important;
          transform: translateY(-4px);
        }
      `}</style>
    </div>
  );
}

export default PublicEssayListPage;
